import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { apiFetch } from '../utils/api'

const CATEGORIES = ['B2B', 'Endkunden']
const TYPES = ['Free', 'Abo', 'Einmalzahlung']
const APP_STORE_BADGES = ['macOS', 'iOS', 'tvOS', 'Android']

const SoftwareProductForm = ({ product, onSave, onCancel, isEditing = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    details: '',
    icon: '',
    category: [],
    type: [],
    images: '',
    visible: true,
    distribution_type: 'download',
    app_store_badge: [],
    app_store_link_macos: '',
    app_store_link_ios: '',
    app_store_link_tvos: '',
    app_store_link_android: '',
    price: '',
    show_demo_button: false,
    show_beta_button: false,
    version: '1.0.0',
    gitlab_project_id: null
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isEditing && product) {
      setFormData({
        name: product.name || '',
        slug: product.slug || '',
        description: product.description || '',
        details: product.details || '',
        icon: product.icon || '',
        category: product.category ? JSON.parse(product.category) : [],
        type: product.type ? JSON.parse(product.type) : [],
        images: product.images || '',
        visible: product.visible === undefined ? true : product.visible,
        distribution_type: product.distribution_type || 'download',
        app_store_badge: product.app_store_badge ? JSON.parse(product.app_store_badge) : [],
        app_store_link_macos: product.app_store_link_macos || '',
        app_store_link_ios: product.app_store_link_ios || '',
        app_store_link_tvos: product.app_store_link_tvos || '',
        app_store_link_android: product.app_store_link_android || '',
        price: product.price || '',
        show_demo_button: product.show_demo_button || false,
        show_beta_button: product.show_beta_button || false,
        version: product.version || '1.0.0',
        gitlab_project_id: product.gitlab_project_id || null
      })
    }
  }, [isEditing, product])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    let processedValue = type === 'checkbox' ? checked : value

    // Convert gitlab_project_id to number or null
    if (name === 'gitlab_project_id') {
      processedValue = value === '' ? null : Number(value)
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }))
    if (error) setError('')
  }

  const handleCheckboxGroup = (name, value) => {
    setFormData(prev => {
      const currentValues = prev[name] || []
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value]
      return { ...prev, [name]: newValues }
    })
    if (error) setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    const { name, slug, description } = formData
    if (!name || !slug || !description) {
      setError('Name, slug and description are required')
      setIsLoading(false)
      return
    }

    // Prepare data for API
    const apiData = {
      ...formData,
      category: JSON.stringify(formData.category),
      type: JSON.stringify(formData.type),
      app_store_badge: JSON.stringify(formData.app_store_badge)
    }

    try {
      const response = await apiFetch(
        isEditing
          ? `/api/admin/software-products/${product.id}`
          : `/api/admin/software-products`,
        { method: isEditing ? 'PUT' : 'POST', body: JSON.stringify(apiData) }
      )
      const data = await response.json()
      if (response.ok) {
        onSave(data.software_product)
      } else {
        setError(data.error || `Failed to ${isEditing ? 'update' : 'create'} product`)
      }
    } catch (err) {
      setError('Network error. Please check backend server.')
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  const isAppStore = formData.distribution_type === 'appstore'

  return (
    <div className="user-form-overlay">
      <div className="user-form-container" style={{ minWidth: '1400px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="user-form-header">
          <h3>{isEditing ? 'Edit Software Product' : 'Add New Software Product'}</h3>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form className="user-form" onSubmit={handleSubmit}>
          {/* Basic Information - 2 columns */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Enter product name" required disabled={isLoading} />
            </div>

            <div className="form-group">
              <label htmlFor="slug">Slug *</label>
              <input type="text" id="slug" name="slug" value={formData.slug} onChange={handleChange} placeholder="Enter URL slug" required disabled={isLoading} />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Short Description *</label>
            <input type="text" id="description" name="description" value={formData.description} onChange={handleChange} placeholder="Enter short description" required disabled={isLoading} />
          </div>

          {/* Icon, Version, Gitlab Project ID - 2 columns */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label htmlFor="icon">Icon</label>
              <input type="text" id="icon" name="icon" value={formData.icon} onChange={handleChange} placeholder="Enter icon name (e.g., Calculator)" disabled={isLoading} />
            </div>

            <div className="form-group">
              <label htmlFor="version">Version</label>
              <input type="text" id="version" name="version" value={formData.version} onChange={handleChange} placeholder="Enter version (e.g., 1.0.0)" disabled={isLoading} />
            </div>

            <div className="form-group">
              <label htmlFor="gitlab_project_id">Gitlab Project ID</label>
              <input type="number" id="gitlab_project_id" name="gitlab_project_id" value={formData.gitlab_project_id || ''} onChange={handleChange} placeholder="Enter Gitlab project ID" disabled={isLoading} />
            </div>

            <div className="form-group"></div>
          </div>

          {/* Category, Type - 2 columns */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <div style={{ marginBottom: '8px' }}>Category *</div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                {CATEGORIES.map(cat => (
                  <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="checkbox"
                      id={`category-${cat}`}
                      checked={formData.category.includes(cat)}
                      onChange={() => handleCheckboxGroup('category', cat)}
                      disabled={isLoading}
                    />
                    <label htmlFor={`category-${cat}`} style={{ margin: 0, fontWeight: 'normal', cursor: 'pointer' }}>
                      {cat}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <div style={{ marginBottom: '8px' }}>Type *</div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                {TYPES.map(t => (
                  <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="checkbox"
                      id={`type-${t}`}
                      checked={formData.type.includes(t)}
                      onChange={() => handleCheckboxGroup('type', t)}
                      disabled={isLoading}
                    />
                    <label htmlFor={`type-${t}`} style={{ margin: 0, fontWeight: 'normal', cursor: 'pointer' }}>
                      {t}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Details and Images - 2 columns */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label htmlFor="details">Details (Markdown)</label>
              <textarea id="details" name="details" value={formData.details} onChange={handleChange} placeholder="Enter detailed description in Markdown format" disabled={isLoading} rows="8" />
            </div>

            <div className="form-group">
              <label htmlFor="images">Images (one URL per line)</label>
              <textarea id="images" name="images" value={formData.images} onChange={handleChange} placeholder="Enter image URLs (one per line)" disabled={isLoading} rows="8" />
            </div>
          </div>

          {/* Show/hide product on website - separate row */}
          <div className="form-group">
            <label htmlFor="visible">Show/hide product on website *</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
              <input
                type="checkbox"
                id="visible"
                name="visible"
                checked={formData.visible}
                onChange={handleChange}
                disabled={isLoading}
              />
              <span>{formData.visible ? 'Show' : 'Hide'}</span>
            </div>
          </div>

          {/* AppStore Fields */}
          {isAppStore && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'start' }}>
                <div className="form-group">
                  <label htmlFor="distribution_type">Distribution Type *</label>
                  <select id="distribution_type" name="distribution_type" value={formData.distribution_type} onChange={handleChange} disabled={isLoading} style={{ width: '100%', padding: '8px', backgroundColor: '#374151', color: '#e5e7eb', border: '1px solid #4b5563', borderRadius: '4px' }}>
                    <option value="appstore">AppStore</option>
                    <option value="download">Download</option>
                  </select>
                </div>

                <div className="form-group">
                  <div style={{ marginBottom: '8px' }}>AppStore Badges</div>
                  <div style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
                    {APP_STORE_BADGES.map(badge => (
                      <div key={badge} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="checkbox"
                          id={`badge-${badge}`}
                          checked={formData.app_store_badge.includes(badge)}
                          onChange={() => handleCheckboxGroup('app_store_badge', badge)}
                          disabled={isLoading}
                        />
                        <label htmlFor={`badge-${badge}`} style={{ margin: 0, fontWeight: 'normal', cursor: 'pointer' }}>
                          {badge}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label htmlFor="app_store_link_macos">AppStore Link (macOS)</label>
                  <input type="text" id="app_store_link_macos" name="app_store_link_macos" value={formData.app_store_link_macos} onChange={handleChange} placeholder="Enter macOS AppStore link" disabled={isLoading} />
                </div>

                <div className="form-group">
                  <label htmlFor="app_store_link_ios">AppStore Link (iOS)</label>
                  <input type="text" id="app_store_link_ios" name="app_store_link_ios" value={formData.app_store_link_ios} onChange={handleChange} placeholder="Enter iOS AppStore link" disabled={isLoading} />
                </div>

                <div className="form-group">
                  <label htmlFor="app_store_link_tvos">AppStore Link (tvOS)</label>
                  <input type="text" id="app_store_link_tvos" name="app_store_link_tvos" value={formData.app_store_link_tvos} onChange={handleChange} placeholder="Enter tvOS AppStore link" disabled={isLoading} />
                </div>

                <div className="form-group">
                  <label htmlFor="app_store_link_android">AppStore Link (Android)</label>
                  <input type="text" id="app_store_link_android" name="app_store_link_android" value={formData.app_store_link_android} onChange={handleChange} placeholder="Enter Android AppStore link" disabled={isLoading} />
                </div>
              </div>
            </>
          )}

          {/* Download Fields */}
          {!isAppStore && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px', alignItems: 'start' }}>
              <div className="form-group">
                <label htmlFor="distribution_type">Distribution Type *</label>
                <select id="distribution_type" name="distribution_type" value={formData.distribution_type} onChange={handleChange} disabled={isLoading} style={{ width: '100%', padding: '8px', backgroundColor: '#374151', color: '#e5e7eb', border: '1px solid #4b5563', borderRadius: '4px' }}>
                  <option value="appstore">AppStore</option>
                  <option value="download">Download</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="price">Price</label>
                <input type="text" id="price" name="price" value={formData.price} onChange={handleChange} placeholder="Enter price (e.g., 9,99 €)" disabled={isLoading} />
              </div>

              <div className="form-group">
                <div style={{ marginBottom: '8px' }}>&nbsp;</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    id="show_demo_button"
                    name="show_demo_button"
                    checked={formData.show_demo_button}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                  <label htmlFor="show_demo_button" style={{ margin: 0, fontWeight: 'normal', cursor: 'pointer' }}>
                    Show Demo Button
                  </label>
                </div>
              </div>

              <div className="form-group">
                <div style={{ marginBottom: '8px' }}>&nbsp;</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    id="show_beta_button"
                    name="show_beta_button"
                    checked={formData.show_beta_button}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                  <label htmlFor="show_beta_button" style={{ margin: 0, fontWeight: 'normal', cursor: 'pointer' }}>
                    Show Beta Button
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel} disabled={isLoading}>Cancel</button>
            <button
              type="submit"
              className="btn-save"
              disabled={isLoading}
            >
              {(() => {
                let saveButtonLabel
                if (isLoading && isEditing) saveButtonLabel = 'Updating...'
                else if (isLoading) saveButtonLabel = 'Creating...'
                else if (isEditing) saveButtonLabel = 'Update'
                else saveButtonLabel = 'Save'
                return saveButtonLabel
              })()}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

SoftwareProductForm.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
    description: PropTypes.string,
    details: PropTypes.string,
    icon: PropTypes.string,
    category: PropTypes.string,
    type: PropTypes.string,
    images: PropTypes.string,
    visible: PropTypes.bool,
    distribution_type: PropTypes.string,
    app_store_badge: PropTypes.string,
    app_store_link_macos: PropTypes.string,
    app_store_link_ios: PropTypes.string,
    app_store_link_tvos: PropTypes.string,
    app_store_link_android: PropTypes.string,
    price: PropTypes.string,
    show_demo_button: PropTypes.bool,
    show_beta_button: PropTypes.bool,
    version: PropTypes.string,
    gitlab_project_id: PropTypes.number
  }),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isEditing: PropTypes.bool
}

export default SoftwareProductForm 